/**
 * WW Timeline — rail fill and sub-item drift for the pinned chapter rail
 * Author: Plaud Tayler — 2026-09-01
 * Version: 1.4.0 - 2026-10-19 19:08 (UTC+8) - Plaud Tayler — cancel interrupted steps and prevent native key scrolling from fighting the active step
 * Version-note: 1.2.0 - 2026-09-16 15:10 (UTC+8) - Plaud Tayler — one gesture steps one sub-item (wheel and keys only, and only while the section owns the viewport)
 * Version-note: 1.1.0 - 2026-09-10 19:05 (UTC+8) - Plaud Tayler — the drift/fill engine runs on
 *   the phone as well, which is pinned now; the tablet band stays flat and stays out
 * Version-note: 1.0.1 - 2026-09-01 15:30 (UTC+8) - Plaud Tayler — first measurement deferred two frames and repeated on load; measuring at bind time marked every chapter done before any scroll
 * Version-note: 1.0.0 - 2026-09-01 14:30 (UTC+8) - Plaud Tayler — initial, adapted from the designer's
 *   prototype (screenshots/0831/plaud-scroll-timeline_7.html)
 *
 * What this does: reads scroll position, drifts each stage's sub-items past a centre line with a
 * smoothstep fade, crossfades the matching media shape, sizes the rail plus its fill from the real
 * dot positions, and — on a wheel or a key, never on touch — steps the page one sub-item at a time.
 *
 * The stepping is the designer's prototype behaviour (screenshots/0915/Sticky_scroll.html) and it
 * exists because without it a reader has to nudge two or three times to land a sub-item on the
 * centre line, and reads the copy mid-fade. With it every rest position is an exact sub-item: one
 * gesture, one item, opacity 1.
 *
 * It was left out of 1.0 because owning the wheel costs real things, so it is scoped as tightly as
 * the behaviour allows:
 *   - only while the section covers the whole viewport. Entering and leaving stay native, so the
 *     reader is never caught by a hijack they did not ask for and can always get out.
 *   - only for wheel and keys. Touch is never intercepted: touchmove stays native, so a phone or a
 *     tablet scrolls exactly as it did.
 *   - only above the phone tier and only under prefers-reduced-motion: no-preference.
 *   - a step that would run past either end of the run is not taken and not prevented, so the
 *     gesture falls through to the browser and the reader leaves the section normally.
 * In-page find, deep links and the scrollbar keep working: any scroll the engine did not start
 * re-syncs the index instead of fighting it.
 *
 * Progressive enhancement: the pin, the layout and every string work with this file absent. It
 * only adds the drift and the fill. It stays out of the way below the desktop gate and under
 * reduced motion, where the CSS has already collapsed the section to a plain stack.
 */
(function () {
  'use strict';
  if (window.__wwTimeline) return;
  window.__wwTimeline = true;

  var TRAVEL = 170; /* px a sub-item drifts per step */
  var FADE = 0.75;  /* steps from the centre to fully faded */

  function smoothstep(k) { return k * k * (3 - 2 * k); }

  function setup(root) {
    var rail = root.querySelector('[data-tl-rail]');
    var fill = root.querySelector('[data-tl-fill]');
    var stages = Array.prototype.slice.call(root.querySelectorAll('[data-tl-stage]'));
    if (!stages.length) return null;

    var groups = stages.map(function (el) {
      var subs = Array.prototype.slice.call(el.querySelectorAll('[data-tl-sub]'));
      return {
        el: el,
        scene: el.querySelector('.ww-tl__scene'),
        dot: el.querySelector('[data-tl-dot]'),
        subs: subs,
        shapes: Array.prototype.slice.call(el.querySelectorAll('[data-tl-shape]')),
        span: Math.max(0, subs.length - 1),
        focalY: 0
      };
    });

    /* Every width is pinned now — the phone has its own scene, the tablet band inherits the
       desktop's — so the only thing left to gate on is whether motion is wanted at all
       - Plaud Tayler - 2026-09-14 18:00 */
    var gate = window.matchMedia('(prefers-reduced-motion: no-preference)');
    var ticking = false;

    /* The pin lasts stage - SCENE, not stage - viewport - Plaud Tayler - 2026-09-21 16:10
       Both readings were the same while the scene was exactly 100svh. Now that it caps at
       --wtl-scene-max, a viewport taller than the cap would have made every range too small: the
       drift would have finished early and the stepping stops would have landed short of where the
       sub-items actually rest. The scene's own height is the only correct denominator. */
    function pinRange(g) {
      var sceneH = (g.scene && g.scene.offsetHeight) || window.innerHeight;
      return g.el.offsetHeight - sceneH;
    }

    function clear() {
      root.classList.remove('ww-tl--drift');
      groups.forEach(function (g) {
        g.subs.forEach(function (s) { s.style.transform = ''; s.style.opacity = ''; });
        g.shapes.forEach(function (v) { v.classList.remove('is-shown'); });
        g.el.classList.remove('is-active', 'is-done');
      });
      if (rail) rail.style.height = '';
      if (fill) fill.style.height = '';
    }

    function update() {
      ticking = false;
      if (!gate.matches) return;

      var vh = window.innerHeight;
      var activeIndex = 0;
      stages.forEach(function (s, i) {
        if (s.getBoundingClientRect().top < vh * 0.985) activeIndex = i;
      });
      stages.forEach(function (s, i) {
        s.classList.toggle('is-active', i === activeIndex);
        s.classList.toggle('is-done', i < activeIndex);
      });

      groups.forEach(function (g) {
        var range = pinRange(g);
        var p = range > 0 ? -g.el.getBoundingClientRect().top / range : 0;
        p = p < 0 ? 0 : p > 1 ? 1 : 0 + p;

        /* a continuous position along the sub-items — 1.4 means "40% of the way from the second
           to the third", which is what makes this read as scrolling rather than as swaps */
        var pos = g.span ? p * g.span : 0;

        g.subs.forEach(function (el, i) {
          var d = pos - i;
          el.style.transform = 'translateY(' + (-d * TRAVEL).toFixed(1) + 'px)';
          var k = 1 - Math.min(1, Math.abs(d) / FADE);
          el.style.opacity = smoothstep(k).toFixed(3);
        });

        var near = Math.round(pos);
        g.shapes.forEach(function (v, i) { v.classList.toggle('is-shown', i === near); });

        if (g.dot) g.focalY = g.dot.getBoundingClientRect().top + g.dot.offsetHeight / 2;
      });

      if (rail && fill) {
        var rTop = rail.getBoundingClientRect().top;
        var railH = Math.max(0, groups[groups.length - 1].focalY - rTop);
        rail.style.height = railH + 'px';
        fill.style.height = Math.max(0, Math.min(railH, groups[activeIndex].focalY - rTop)) + 'px';
      }
    }

    function onScroll() {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }

    /* Two frames before the first measurement: at bind time the sticky scenes have not been
       laid out at their final heights, and measuring then marks every chapter done before the
       reader has scrolled anywhere. Same guard the theme's scene loader uses. */
    function measureSoon() {
      requestAnimationFrame(function () { requestAnimationFrame(update); });
    }

    function applyGate() {
      if (gate.matches) { root.classList.add('ww-tl--drift'); measureSoon(); }
      else { clear(); }
    }

    /* ---- stepping: one gesture, one sub-item -------------------------------------------
       The stops are derived from the same arithmetic update() uses rather than from markers in
       the markup: update() reads p = -stageTop / (stageHeight - sceneHeight) and centres sub i at
       p = i / span, so sub i rests at stageTopAbs + range * i / span. Deriving them keeps the
       stops and the drift from ever disagreeing. */
    var stepGate = window.matchMedia('(min-width: 769px) and (prefers-reduced-motion: no-preference)');
    var section = root.closest('.shopify-section') || root;
    var points = [];
    var index = 0;
    var busy = false;
    // Native scroll interruptions must release the animation - Plaud Tayler - 2026-10-19 19:08 - Begin
    var stepFrame = 0;
    var stepTail = 0;
    var expectedY = null;

    function cancelStep() {
      cancelAnimationFrame(stepFrame);
      clearTimeout(stepTail);
      stepFrame = 0;
      stepTail = 0;
      expectedY = null;
      busy = false;
    }
    // Native scroll interruptions must release the animation - Plaud Tayler - 2026-10-19 19:08 - End

    function measurePoints() {
      var y = window.scrollY || window.pageYOffset;
      var list = [];
      groups.forEach(function (g) {
        var top = g.el.getBoundingClientRect().top + y;
        var range = pinRange(g);
        if (range <= 0 || !g.span) { list.push(Math.round(top)); return; }
        for (var i = 0; i <= g.span; i++) list.push(Math.round(top + range * (i / g.span)));
      });
      list.sort(function (a, b) { return a - b; });
      points = list;
    }

    function nearestIndex() {
      var y = window.scrollY || window.pageYOffset, best = 0, bd = Infinity;
      for (var i = 0; i < points.length; i++) {
        var d = Math.abs(points[i] - y);
        if (d < bd) { bd = d; best = i; }
      }
      return best;
    }

    /* The run is owned only while the section covers the viewport; on the way in and on the way
       out the browser keeps the wheel, which is what lets the reader enter and leave normally. */
    function owns() {
      if (!stepGate.matches || !points.length) return false;
      var r = section.getBoundingClientRect();
      return r.top <= 0 && r.bottom >= window.innerHeight;
    }

    function easeInOutCubic(t) {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    function go(to) {
      var from = window.scrollY || window.pageYOffset;
      var dist = points[to] - from;
      index = to;
      if (Math.abs(dist) < 1) return;
      /* a longer hop (the gap between two chapters) gets a little more time than a step */
      var dur = Math.min(700, Math.max(340, 300 + Math.abs(dist) * 0.28));
      var t0 = null;
      busy = true;
      expectedY = from;
      (function frame(now) {
        // Yield when native input, layout or the motion gate interrupts a step - Plaud Tayler - 2026-10-19 19:08 - Begin
        if (!stepGate.matches || Math.abs(window.scrollY - expectedY) > 1) {
          cancelStep();
          return;
        }
        // Yield when native input, layout or the motion gate interrupts a step - Plaud Tayler - 2026-10-19 19:08 - End
        if (t0 === null) t0 = now;
        var k = Math.min(1, (now - t0) / dur);
        window.scrollTo(0, from + dist * easeInOutCubic(k));
        expectedY = window.scrollY;
        if (k < 1) stepFrame = requestAnimationFrame(frame);
        /* the tail swallows what is left of a trackpad's momentum */
        else stepTail = setTimeout(cancelStep, 140);
      })(performance.now ? performance.now() : Date.now());
    }

    function step(dir) {
      var to = index + dir;
      /* past either end: do not take it and do not prevent it — the gesture belongs to the page */
      if (to < 0 || to > points.length - 1) return false;
      go(to);
      return true;
    }

    window.addEventListener('wheel', function (e) {
      if (!owns() || e.ctrlKey) return;            /* ctrl+wheel is the browser's zoom */
      if (busy) { e.preventDefault(); return; }    /* momentum after a step is not a new gesture */
      if (Math.abs(e.deltaY) < 4) return;
      index = nearestIndex();
      if (step(e.deltaY > 0 ? 1 : -1)) e.preventDefault();
    }, { passive: false });

    var KEYS = { ArrowDown: 1, PageDown: 1, ' ': 1, ArrowUp: -1, PageUp: -1 };
    window.addEventListener('keydown', function (e) {
      if (!owns() || e.metaKey || e.ctrlKey || e.altKey) return;
      var el = document.activeElement;
      /* never take a key away from something that is being typed into or operated */
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT|BUTTON|A)$/.test(el.tagName))) return;
      var dir = KEYS[e.key];
      if (!dir) return;
      if (busy) { e.preventDefault(); return; }
      index = nearestIndex();
      if (step(dir)) e.preventDefault();
    });

    /* Anything that moved the page without us — the scrollbar, a deep link, in-page find, touch —
       re-syncs the index rather than being corrected. */
    // Keep external input authoritative while a step is in flight - Plaud Tayler - 2026-10-19 19:08 - Begin
    window.addEventListener('scroll', function () {
      if (busy && Math.abs(window.scrollY - expectedY) > 1) cancelStep();
      if (!busy) index = nearestIndex();
    }, { passive: true });
    window.addEventListener('pointerdown', cancelStep, { passive: true });
    window.addEventListener('touchstart', cancelStep, { passive: true });
    window.addEventListener('resize', function () { cancelStep(); measurePoints(); }, { passive: true });
    // Keep external input authoritative while a step is in flight - Plaud Tayler - 2026-10-19 19:08 - End
    window.addEventListener('load', measurePoints, { once: true });
    measurePoints();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    if (typeof gate.addEventListener === 'function') gate.addEventListener('change', applyGate);
    else if (typeof gate.addListener === 'function') gate.addListener(applyGate);

    applyGate();
    /* Late-arriving images change every offset the rail is derived from. */
    window.addEventListener('load', measureSoon, { once: true });
    return { update: update };
  }

  function bindAll() {
    var roots = document.querySelectorAll('.ww-tl');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].__wwTlBound) continue;
      roots[i].__wwTlBound = true;
      setup(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAll, { once: true });
  } else {
    bindAll();
  }
  document.addEventListener('shopify:section:load', bindAll);
})();
